import type { TOC } from '@ember/component/template-only';
import { fn } from '@ember/helper';
import {
  PrismButton,
  PrismCard,
  PrismLoadingSpinner,
  PrismMessage,
  PrismModal,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import SettingsPmsIntegrationSeasonalStrategyCard from 'lumen/components/settings/pms-integration/seasonal-strategy-card';
import type { RecommendationStrategy } from 'lumen/models';

interface SettingsPmsIntegrationSeasonalRoomHierarchyModalSignature {
  Args: {
    isLoading: boolean;
    isReadOnly: boolean;
    offsetTypeLabel: string;
    onClose: () => void;
    onCreateSeasonalStrategy: () => void;
    onDeleteSeasonalStrategy: (
      seasonalStrategy: RecommendationStrategy,
    ) => void;
    seasonalStrategies: RecommendationStrategy[];
  };
}

const SettingsPmsIntegrationSeasonalRoomHierarchyModal: TOC<SettingsPmsIntegrationSeasonalRoomHierarchyModalSignature> =
  <template>
    <PrismModal
      @onClose={{@onClose}}
      @title={{t
        "pricing_assistant.recommendations.price_range.subtitle.seasonal_strategies"
      }}
    >
      <:default>
        <div class="modal--padded modal--medium">
          {{#if @isLoading}}
            <div
              class="flex items-center justify-center p-800"
              data-test-id="seasonal-strategies-loading"
            >
              <PrismLoadingSpinner />
            </div>
          {{else}}
            {{! Offset type is read-only here; the page dropdown stays its source of truth. }}
            <div class="mb-200 flex flex-row items-center justify-between">
              <p class="text-200--bold">{{t
                  "pricing_assistant.settings.panel.section.rooms_mapping.room_types_offsets"
                }}</p>
              <p class="text-200--regular" data-test-id="offset-type">
                {{@offsetTypeLabel}}
              </p>
            </div>

            {{#if @seasonalStrategies.length}}
              {{#unless @isReadOnly}}
                <div class="mb-200 flex flex-row justify-end">
                  <PrismButton
                    @buttonType="primary"
                    @label={{t
                      "pricing_assistant.recommendations.create_seasonal_strategy"
                    }}
                    @onClick={{@onCreateSeasonalStrategy}}
                    data-test-id="create-seasonal-strategy-button"
                  />
                </div>
              {{/unless}}

              <div
                class="flex max-h-[560px] flex-col gap-200 overflow-y-auto"
                data-test-id="seasonal-strategy-list"
              >
                {{#each @seasonalStrategies as |seasonalStrategy|}}
                  <SettingsPmsIntegrationSeasonalStrategyCard
                    @isReadOnly={{@isReadOnly}}
                    @onDelete={{fn @onDeleteSeasonalStrategy seasonalStrategy}}
                    @strategy={{seasonalStrategy}}
                  />
                {{/each}}
              </div>
            {{else}}
              <PrismCard class="flex items-center justify-center pt-400">
                <:content>
                  <PrismMessage
                    @description={{t
                      "pricing_assistant.recommendations.price_range.empty_state.description"
                    }}
                    @illustration="100x100_chart"
                    @title={{t
                      "pricing_assistant.recommendations.empty_state.title"
                    }}
                    data-test-id="seasonal-strategies-empty-state"
                  >
                    <:primaryAction>
                      {{#unless @isReadOnly}}
                        <PrismButton
                          @buttonType="primary"
                          @label={{t
                            "pricing_assistant.recommendations.create_seasonal_strategy"
                          }}
                          @onClick={{@onCreateSeasonalStrategy}}
                          data-test-id="create-seasonal-strategy-button"
                        />
                      {{/unless}}
                    </:primaryAction>
                  </PrismMessage>
                </:content>
              </PrismCard>
            {{/if}}
          {{/if}}
        </div>
      </:default>
    </PrismModal>
  </template>;

export default SettingsPmsIntegrationSeasonalRoomHierarchyModal;
